import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "./Table.css";
import TaskPopUp from "../Popup/TaskPopUp";
import getStatusColorClass from "../../util/GetStatusColor";
import { saveAs } from "file-saver";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { toast } from "react-hot-toast";

const TasksList = () => {
  // Removed { route } prop
  //let { selectedRowId } = useParams(); // Removed useParams
  const [selectedRowId, setSelectedRowId] = useState("all"); // Initialize locally

  let arr = [];
  let arrAsJsonStringUid = [];
  let arrAsJsonStringOutlet = [];

  const [pincodeFilter, setPincodeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(null);
  const toggleMenu = (id) => {
    setIsMenuOpen(id);
  };

  // Using an object to manage filters, more scalable
  const [filters, setFilters] = useState({
    pincode: "",
    status: "",
    startDate: "", // Set initial start date
    endDate: "", // Set initial end date
    channelManager: "",
    vendor: "",
    outlet: "",
    salesManager: "",
    channel_programme: "",
  });

  const [tableData, setTableData] = useState([]);
  const [sellerData, setSellerData] = useState([]);
  const [channel_programme, setChannel_programme] = useState([]);

  //const { channelManager: initialChannelManager, vendor: initialVendor, outlet: initialOutlet } = Object.fromEntries(new URLSearchParams(window.location.search)); //removed this

  const [initialFiltersLoaded, setInitialFiltersLoaded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const columnHelper = createColumnHelper();

  const qucickApprove = async (task) => {
    try {
      const endpoint =
        task.status === "Waiting For Approval"
          ? `/task/${task._id}/job/before/approve`
          : `/task/${task._id}/job/after/approve`;
      const jobId = task.jobs.map((job) => job._id);

      console.log(endpoint, task);
      await axios.put(
        `${endpoint}`,
        { jobId, approved: true },
        {
          headers: {
            authorization: localStorage.getItem("user-token"),
          },
        }
      );
      toast.success(
        `Job ${
          task.status === "Waiting For Approval" ? "Before" : "After"
        } Approved!`
      );
      getTasksList(); // Refresh data
    } catch (error) {
      console.error("Error approving job:", error);
      toast.error("Failed to approve job.");
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("outletWorkDetails.gccNum", {
        header: () => "GCC Code",
        cell: (info) => info.getValue(),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor("outletWorkDetails.outletName", {
        header: () => "Outlet Name",
        cell: (info) => info.getValue(),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor("outletWorkDetails.pinCode", {
        header: () => "Pin Code",
        cell: (info) => info.getValue(),
        footer: (info) => info.column.id,
        filterFn: (row, columnId, filterValue) => {
          return String(row.getValue(columnId)).startsWith(filterValue);
        },
      }),
      columnHelper.accessor("vendorData.userName", {
        header: () => "Vendor",
        cell: (info) => info.getValue(),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor("jobs.length", {
        header: () => "Total tasks",
        cell: (info) => info.getValue(),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor("status", {
        header: () => "Status",
        cell: (info) => {
          const statusColorClass = getStatusColorClass(info.getValue());
          return (
            <div className="chip">
              <div
                className={`badge badge-outline badge-${statusColorClass.color} text-${statusColorClass.color}`}
              >
                {statusColorClass.title}
              </div>
            </div>
          );
        },
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor("createdAt", {
        header: () => "Created At",
        cell: (info) =>
          info.getValue() &&
          new Date(info.getValue()).toLocaleDateString("en-IN", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          }),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor("updatedAt", {
        header: () => "Updated At",
        cell: (info) =>
          info.getValue() &&
          new Date(info.getValue()).toLocaleDateString("en-IN", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          }),
        footer: (info) => info.column.id,
      }),
      columnHelper.display({
        id: "menu",
        header: () => "",
        cell: ({ row }) => (
          <>
            <ul className="menu lg:menu-horizontal bg-base-200 rounded-box relative">
              <li>
                <details>
                  <summary>
                    Options
                    {row.original.notification && (
                      <span
                        className="badge badge-xs badge-info p-2"
                        style={{ backgroundColor: "red", color: "white" }}
                      >
                        New
                      </span>
                    )}
                  </summary>
                  <ul style={{ zIndex: 6900, position: "absolute" }}>
                    <li>
                      <Link
                        to={`/task/${row.original._id}`}
                        state={{
                          ids: tableData.map((v) => v._id),
                          from: location.pathname + location.search,
                        }}
                      >
                        <span className="text-bold text-xl"> ↸ </span>
                        View Task
                      </Link>
                    </li>
                    <li
                      className={`${
                        !row.original.notification && "disable-links"
                      }`}
                    >
                      <a onClick={() => qucickApprove(row.original)}>
                        <span className="font-bold text-xl"> ✓ </span>
                        Quick Approve
                      </a>
                    </li>
                  </ul>
                </details>
              </li>
            </ul>
          </>
        ),
      }),
    ],
    [qucickApprove, isMenuOpen]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      globalFilter: pincodeFilter,
    },
    onGlobalFilterChange: setPincodeFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const getTasksList = async () => {
    const userToken = localStorage.getItem("user-token");

    const headers = {
      authorization: userToken,
    };
    let bUrl = `/task`;

    // Use the filters state to build the query params
    const queryParams = {
      search: filters.pincode,
      status: filters.status,
      startDate: filters.startDate,
      endDate: filters.endDate,
      gccNum: arrAsJsonStringOutlet,
      channelManager: filters.channelManager,
      outlet: filters.outlet,
      salesManager: filters.salesManager,
      channel_programme: filters.channel_programme,
      vendor: filters.vendor,
    };

    // Remove empty query parameters
    Object.keys(queryParams).forEach((key) => {
      if (
        queryParams[key] === "" ||
        queryParams[key] === null ||
        queryParams[key] === undefined
      ) {
        delete queryParams[key];
      }
    });

    await axios
      .get(bUrl, {
        headers: headers,
        params: queryParams,
      })
      .then(async (response) => {
        await setTableData(response.data.data);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const getSellerList = async () => {
    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      const response = await axios.get(`/sellers`, { headers });
      setSellerData(response.data);
    } catch (error) {
      console.error("Error fetching seller data:", error);
      toast.error("Error fetching seller data.");
    }
  };

  const getChannelProgramsList = async () => {
    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };

    try {
      const response = await axios.get(`/channel_programs`, { headers });
      setChannel_programme(response.data.data);
    } catch (error) {
      console.error("Error fetching seller data:", error);
      toast.error("Error fetching seller data.");
    }
  };

  useEffect(() => {
    if (selectedRowId !== "all") {
      if (selectedRowId.startsWith("G,")) {
        selectedRowId = selectedRowId.substring(2);
        arr = JSON.stringify(selectedRowId.split(","));
        arrAsJsonStringOutlet = arr;
      } else {
        arrAsJsonStringUid = arr;
      }
    }

    //load initial filters
    const urlParams = new URLSearchParams(window.location.search);
    const initialChannelManager = urlParams.get("channelManager") || "";
    const initialVendor = urlParams.get("vendor") || "";
    const initialOutlet = urlParams.get("outlet") || "";
    const initialStartDate = urlParams.get("startDate") || "";
    const initialEndDate = urlParams.get("endDate") || "";
    const salesManager = urlParams.get("salesManager") || "";
    const channel_programme = urlParams.get("channel_programme") || "";

    setFilters((prevFilters) => ({
      ...prevFilters,
      channelManager: initialChannelManager,
      vendor: initialVendor,
      outlet: initialOutlet,
      salesManager: salesManager,
      startDate: initialStartDate,
      endDate: initialEndDate,
      channel_programme,
    }));
    setInitialFiltersLoaded(true);
  }, [selectedRowId]);

  //useEffect for fetching data
  useEffect(() => {
    if (initialFiltersLoaded) {
      getTasksList().then(async (r) => {
        console.log("print data");
      });
      getSellerList();
      getChannelProgramsList();
    }
  }, [filters, initialFiltersLoaded]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterType]: value,
    }));

    //Update URL parameters based on filter changes
    const urlParams = new URLSearchParams(window.location.search);
    if (value) {
      urlParams.set(filterType, value);
    } else {
      urlParams.delete(filterType);
    }
    window.history.pushState(
      {},
      "",
      `${window.location.pathname}?${urlParams.toString()}`
    );
  };

  const removeFilter = (filterType) => {
    handleFilterChange(filterType, "");
  };

  const downloadCSV = async () => {
    if (!tableData || tableData.length === 0) {
      alert("No data to download.");
      return;
    }

    // Define the headers you want in the CSV
    const headers = [
      "GCC ID",
      "Outlet Name",
      "Cluster",
      "City",
      "State",
      "STL Name",
      "SM/GSM Name",
      "Channel Manager",
      "Created At",
      "Updated At",
      "Vendor Name",
      "Channel Program",
      "Brand used",
      "Job Status",
      "Job Count",
      "Days since last Status Change",
    ];

    // Process each row to extract only the required fields
    const rows = tableData.map((item) => {
      // Get channel program from the first job's before data (if exists)
      const channelProgram =
        item.jobs && item.jobs[0] && item.jobs[0].before
          ? item.jobs[0].before.channel_programme
          : "";

      // Calculate days since last status change
      const lastUpdated = new Date(item.updatedAt);
      const today = new Date();
      const daysSinceLastChange = Math.floor(
        (today - lastUpdated) / (1000 * 60 * 60 * 24)
      );

      return {
        "GCC ID": item.outletWorkDetails?.gccNum || "",
        "Outlet Name": item.outletWorkDetails?.outletName || "",
        Cluster: "", // This field doesn't exist in the sample data
        City: item.outletWorkDetails?.town || "",
        State: item.vendorData?.state || "",
        "STL Name": item.salesManagerData?.sellerCode || "",
        "SM/GSM Name": item.salesManagerData?.sellerCode || "", // This field ??
        "Channel Manager": item.salesManagerData?.channelManagerID || "",
        "Created At": item.createdAt || "",
        "Updated At": item.updatedAt || "",
        "Vendor Name":
          item.vendorData?.companyName || item.vendorData?.userName || "",
        "Channel Program": channelProgram,
        "Brand used": item.brand || item.approvedBrand || "",
        "Job Status": item.status || "",
        "Job Count": item.jobs ? item.jobs.length : 0,
        "Days since last Status Change": daysSinceLastChange,
      };
    });

    // Convert to CSV
    let csvContent = headers.join(",") + "\n";

    rows.forEach((row) => {
      const rowValues = headers.map((header) => {
        // Escape quotes and wrap in quotes if contains commas
        const value = row[header] !== undefined ? row[header] : "";
        const escapedValue = String(value).replace(/"/g, '""');
        return `"${escapedValue}"`;
      });
      csvContent += rowValues.join(",") + "\n";
    });

    // Download the CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "outlet_data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //Generate Applied Filters Chips
  const generateFilterChips = () => {
    return Object.keys(filters)
      .filter((key) => filters[key] !== "")
      .map((key) => {
        let printKey = "";
        let vaule = "";
        if (key === "channelManager") {
          printKey = "Channel Manager";
          vaule = "";
        }
        if (key === "vendor") {
          printKey = "Vendor";
          vaule = tableData?.[0]?.vendorData?.userName || "";
        }
        if (key === "outlet") {
          printKey = "Outlet";
          vaule = tableData?.[0]?.outletWorkDetails?.outletName || "";
        }
        if (key === "salesManager") {
          printKey = "STL";
          vaule = tableData?.[0]?.salesManagerData.sellerCode || "";
        }
        return (
          <div key={key} className="chip mr-2">
            {printKey || key}: {vaule || filters[key]}
            <button
              className="btn btn-xs ml-2"
              onClick={() => removeFilter(key)}
            >
              x
            </button>
          </div>
        );
      });
  };

  return (
    <div className={"flex"}>
      <div className="w-[90vw] ml-[7vw] mt-[2vh] mr-0 overflow-auto rounded-lg shadow-md">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold mb-4">All Jobs</h1>
          <div className="flex items-center space-x-4">
            <a
              onClick={() =>
                navigate(
                  `/sharedTask/${tableData?.map((t) => t._id).join(",")}`
                )
              }
              className="btn btn-primary"
            >
              <span className="text-bold text-xl"> 🗎 </span> Download PDF
            </a>
            <a onClick={downloadCSV} className="btn btn-primary">
              <span className="text-bold text-xl"> ↯ </span> Download CSV
            </a>
          </div>
        </div>
        <div className="flex items-center space-x-4 p-4">
          <div className="flex flex-col items-start w-full">
            <label className=" font-bold text-sm text-left">Search</label>
            <input
              type="text"
              value={filters.pincode}
              onChange={(e) => handleFilterChange("pincode", e.target.value)}
              placeholder="Search by Pincode, GCC, address, outlet name..."
              className="input input-bordered w-full"
            />
          </div>
          <div className="flex flex-col items-start">
            <label className=" font-bold text-sm text-left">Status</label>
            <select
              className="select select-bordered w-full"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="onGoing">Ongoing</option>
              <option value="Waiting For Approval">Waiting For Approval</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="flex flex-col items-start w-[20vw]">
            <label className=" font-bold text-sm text-left">Programs</label>
            <select
              className="select select-bordered w-full"
              value={filters.channel_programme}
              onChange={(e) =>
                handleFilterChange("channel_programme", e.target.value)
              }
            >
              <option value="">All Programms</option>
              {channel_programme.map((seller) => (
                <option value={seller.name}>{seller.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col items-start">
            <label className=" font-bold text-sm text-left">STL</label>
            <select
              className="select select-bordered w-full"
              value={filters.salesManager}
              onChange={(e) =>
                handleFilterChange("salesManager", e.target.value)
              }
            >
              <option value="">All STL</option>
              {sellerData.map((seller) => (
                <option value={seller._id}>{seller.sellerCode}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col items-start">
            {" "}
            {/* Changed to flex-col */}
            <label className="font-bold text-sm text-left">Start Date</label>
            <input
              type="date"
              className="input input-bordered w-full bg-black text-white"
              onKeyDown={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
            />
          </div>
          <div className="flex flex-col items-start">
            <label className="font-bold text-sm text-left">End Date</label>
            <input
              type="date"
              className="input input-bordered w-full bg-black text-white"
              value={filters.endDate}
              onKeyDown={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
            />
          </div>
        </div>
        <div className="p-4 flex items-start">{generateFilterChips()}</div>
        <div className="">
          <table className="table w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : ""
                      }`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {{
                        asc: " ↑",
                        desc: " ↓",
                      }[header.column.getIsSorted()] ?? null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  //onClick={() => newTaskDetails(row.original)}
                  className="hover:bg-gray-100 cursor-pointer transition-colors duration-200"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="join p-4">
          <button
            className="join-item btn"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </button>
          <button
            className="join-item btn"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="join-item btn"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
          <button
            className="join-item btn"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </button>
          <span
            style={{ alignItems: "center" }}
            className="flex input input-bordered join-item"
          >
            Page{" "}
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </span>
          <span className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              max={table.getPageCount()}
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="input input-bordered w-24"
            />
          </span>
          <select
            className="select select-bordered"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default TasksList;
